import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { X, Save, RotateCcw } from "lucide-react";
import { SHEET_5871 } from "@/lib/avery5871";

const PRESETS = { AVERY_5871: SHEET_5871 };

function pageDims(layout) {
  return layout.orientation === "LANDSCAPE" ? { w: 11, h: 8.5 } : { w: 8.5, h: 11 };
}
// Layout (drawing) canvas — swapped when content is rotated 90°/270°.
function layoutDims(layout) {
  const p = pageDims(layout);
  return (layout.content_rotation === 90 || layout.content_rotation === 270) ? { w: p.h, h: p.w } : p;
}

function SheetVisual({ layout, calX = 0, calY = 0 }) {
  const { w: pageW, h: pageH } = pageDims(layout);
  const { w: canvasW, h: canvasH } = layoutDims(layout);
  const cols = Math.max(1, Number(layout.cols) || 1);
  const rows = Math.max(1, Number(layout.rows) || 1);
  const tW = Number(layout.ticket_w) || 3.5;
  const tH = Number(layout.ticket_h) || 2;
  const mX = (Number(layout.margin_x) || 0) + (Number(calX) || 0);
  const mY = (Number(layout.margin_y) || 0) + (Number(calY) || 0);
  const gX = Number(layout.gap_x) || 0;
  const gY = Number(layout.gap_y) || 0;
  const slots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      slots.push({
        x: mX + c * (tW + gX),
        y: mY + r * (tH + gY),
        w: tW,
        h: tH,
        idx: r * cols + c + 1,
      });
    }
  }
  const pct = (v, base) => `${(v / base) * 100}%`;
  // We render the LOGICAL canvas (so tickets display correctly) inside a wrapper
  // sized to the physical page aspect ratio. When content_rotation=90 the inner
  // canvas is rotated -90° via CSS so the on-screen preview matches the printed
  // physical page orientation.
  const rotated = layout.content_rotation === 90 || layout.content_rotation === 270;
  return (
    <div className="relative mx-auto flex items-center justify-center" style={{ aspectRatio: `${pageW} / ${pageH}`, width: "100%", maxWidth: layout.orientation === "LANDSCAPE" ? 640 : 400 }} data-testid="sheet-visual-wrap">
      <div className="relative rounded-lg bg-white border border-slate-500 shadow-inner" style={{
        aspectRatio: `${canvasW} / ${canvasH}`,
        width: rotated ? "calc(100% * " + (pageH / pageW) + " * " + (canvasW / canvasH) + ")" : "100%",
        height: rotated ? "100%" : "auto",
        transform: layout.content_rotation === 90 ? "rotate(-90deg)" : layout.content_rotation === 270 ? "rotate(90deg)" : layout.content_rotation === 180 ? "rotate(180deg)" : "none",
        transformOrigin: "center",
      }} data-testid="sheet-visual">
        <div className="absolute top-1 left-2 text-[9px] text-red-600 font-bold uppercase tracking-widest">▲ Top of Sheet</div>
        <div className="absolute bottom-1 left-2 text-[9px] text-red-600 font-bold uppercase tracking-widest">▼ Printer Feed Edge</div>
        {slots.map((s) => (
          <div key={s.idx}
            className="absolute border border-orange-400/70 bg-orange-500/10 flex items-center justify-center text-[9px] font-bold text-orange-600/70"
            style={{ left: pct(s.x, canvasW), top: pct(s.y, canvasH), width: pct(s.w, canvasW), height: pct(s.h, canvasH) }}
            data-testid={`slot-${s.idx}`}
          >{s.idx}</div>
        ))}
        <div className="absolute right-1 bottom-1 text-[9px] text-slate-500">{canvasW}" × {canvasH}" canvas · {layout.orientation}</div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, step = 0.01, min, max, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      <Input type="number" step={step} min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="bg-[#0B1120] border-slate-700 text-white h-9"
        data-testid={testid} />
    </div>
  );
}

export default function SheetLayoutModal({ initial, onClose, onSaved }) {
  const [layout, setLayout] = useState({ ...PRESETS.AVERY_5871, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const patch = (upd) => setLayout((p) => ({ ...p, ...upd }));

  const loadPreset = (key) => setLayout({ ...PRESETS[key] });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/print/sheet-settings", { sheet_layout: layout });
      toast.success("Sheet layout saved");
      onSaved?.(layout);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="sheet-layout-modal">
      <div className="bg-[#0B1120] border border-slate-700 rounded-2xl w-full max-w-6xl my-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Configure Sheet Layout</div>
            <div className="text-white font-bold text-lg">Avery 5871 · Page geometry & card grid</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="sheet-close" aria-label="Close">
            <X />
          </button>
        </div>

        <div className="p-5 grid lg:grid-cols-[1.2fr_340px] gap-5">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-slate-400">Preset:</span>
              <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 text-slate-200"
                onClick={() => loadPreset("AVERY_5871")} data-testid="preset-5871">Avery 5871 · Portrait (2×5, 3.5"×2")</Button>
            </div>
            <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-4">
              <SheetVisual layout={layout} />
              <div className="text-xs text-slate-500 mt-3">
                Physical page: <b className="text-slate-300">{pageDims(layout).w}" × {pageDims(layout).h}"</b> ·
                Slots: <b className="text-slate-300">{layout.cols * layout.rows}</b> ·
                Each ticket: <b className="text-slate-300">{layout.ticket_w}" × {layout.ticket_h}"</b>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg bg-[#131D33] border border-slate-800 p-3">
              <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-2">Ticket Grid</div>
              <div className="grid grid-cols-2 gap-2">
                <NumField label="Columns" value={layout.cols} step={1} min={1} max={6} onChange={(v) => patch({ cols: Math.max(1, v | 0) })} testid="inp-cols" />
                <NumField label="Rows"    value={layout.rows} step={1} min={1} max={20} onChange={(v) => patch({ rows: Math.max(1, v | 0) })} testid="inp-rows" />
                <NumField label="Ticket W (in)" value={layout.ticket_w} onChange={(v) => patch({ ticket_w: v })} testid="inp-tW" />
                <NumField label="Ticket H (in)" value={layout.ticket_h} onChange={(v) => patch({ ticket_h: v })} testid="inp-tH" />
              </div>
            </div>

            <div className="rounded-lg bg-[#131D33] border border-slate-800 p-3">
              <div className="text-xs uppercase tracking-widest text-sky-400 font-bold mb-2">Margins & Spacing</div>
              <div className="grid grid-cols-2 gap-2">
                <NumField label="Margin X (in)" value={layout.margin_x} onChange={(v) => patch({ margin_x: v })} testid="inp-mx" />
                <NumField label="Margin Y (in)" value={layout.margin_y} onChange={(v) => patch({ margin_y: v })} testid="inp-my" />
                <NumField label="H. Spacing (in)" value={layout.gap_x} onChange={(v) => patch({ gap_x: v })} testid="inp-gx" />
                <NumField label="V. Spacing (in)" value={layout.gap_y} onChange={(v) => patch({ gap_y: v })} testid="inp-gy" />
              </div>
            </div>

            <details className="rounded-lg bg-[#131D33] border border-slate-800 p-3">
              <summary className="text-xs uppercase tracking-widest text-slate-400 font-bold cursor-pointer">Advanced · Sheet Content Rotation</summary>
              <div className="mt-2 grid grid-cols-4 gap-1">
                {[0, 90, 180, 270].map((r) => (
                  <button key={r} onClick={() => patch({ content_rotation: r })}
                    className={`h-9 rounded text-sm font-bold ${layout.content_rotation === r ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                    data-testid={`rot-${r}`}>
                    {r}°
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-2">Rotates all content on the page. Useful if the printer feeds the sheet from a different edge.</p>
            </details>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => loadPreset("AVERY_5871")}
                className="bg-slate-900 border-slate-700 text-slate-200 flex-1" data-testid="reset-sheet">
                <RotateCcw size={14} className="mr-1" /> Reset
              </Button>
              <Button onClick={save} disabled={saving}
                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold flex-1"
                data-testid="save-sheet">
                <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Sheet Layout"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { SheetVisual, PRESETS };
